import { format } from 'date-fns';

/**
 * Generate responsive sizes for MUI breakpoints.
 *
 * This function supports two modes:
 *
 * **1. Auto-generate mode**
 *    - Provide `diff` and `xl`
 *    - It automatically reduces the size by `diff` for each breakpoint:
 *      xl → lg → md → sm → xs
 *
 * **2. Override mode**
 *    - You may optionally supply values for lg, md, sm, xs
 *    - Any provided override replaces the auto-generated value
 *
 * @param diff - The decrement applied for each lower breakpoint (e.g., 1px per step)
 * @param xl - The base value for the xl breakpoint (required)
 * @param lg - (optional) Override value for lg breakpoint
 * @param md - (optional) Override value for md breakpoint
 * @param sm - (optional) Override value for sm breakpoint
 * @param xs - (optional) Override value for xs breakpoint
 *
 * @returns An object containing px values for all MUI breakpoints:
 *          { xl: "40px", lg: "39px", md: "38px", sm: "37px", xs: "36px" }
 *
 * @example
 * // Auto-generated values (xl=40, diff=1)
 * sizeByBreakpoint(1, 40)
 * → { xl: "40px", lg: "39px", md: "38px", sm: "37px", xs: "36px" }
 *
 * @example
 * // Override lg only
 * sizeByBreakpoint(1, 40, 36)
 * → { xl: "40px", lg: "36px", md: "35px", sm: "34px", xs: "33px" }
 */
export const sizeByBreakpoint = (diff: number, xl: number, lg?: number, md?: number, sm?: number, xs?: number) => {
  const breakpointNames = ['xl', 'lg', 'md', 'sm', 'xs'] as const;
  const overrideValues = { xl, lg, md, sm, xs };

  // Auto-generate values starting from xl ↓ downwards by diff
  const autoValues: Record<string, string> = {};
  let current = xl;

  breakpointNames.forEach((bp, index) => {
    if (index === 0) {
      autoValues[bp] = `${xl}px`;
    } else {
      current -= diff;
      autoValues[bp] = `${current}px`;
    }
  });

  // Merge auto-generated with overrides
  const result: Record<string, string> = {};
  breakpointNames.forEach((bp) => {
    result[bp] = overrideValues[bp] !== undefined ? `${overrideValues[bp]}px` : autoValues[bp];
  });

  return result;
};

/**
 * Generates responsive text sizes for all breakpoints from a maximum size.
 *
 * Takes your largest text size (for desktop) and automatically scales it down
 * for smaller screens. Converts px to rem and ensures text stays readable on mobile.
 *
 * @param maxSize - The maximum text size in pixels (used for xl breakpoint)
 * @param minSize - Optional minimum size in pixels (defaults to 8px). Pass 0 for no minimum.
 *
 * @returns Object with text sizes in rem for each breakpoint (xl, lg, md, sm, xs)
 *
 * @example
 * // Auto-scale from 48px with 8px minimum
 * const heading = textSizeByBreakpoint(48);
 * // { xl: "3.000rem", lg: "2.700rem", md: "2.250rem", sm: "1.950rem", xs: "1.650rem" }
 *
 * @example
 * // Small text with no minimum constraint
 * const tiny = textSizeByBreakpoint(10, 0);
 * // Scales all the way down to 5.5px on mobile
 *
 * @example
 * // Custom minimum of 14px for body text
 * const body = textSizeByBreakpoint(18, 14);
 * // Never goes below 14px on any screen
 */
export const textSizeByBreakpoint = (maxSize: number, minSize?: number) => {
  // Convert px to rem (assuming 16px = 1rem)
  const pxToRem = (px: number) => px / 16;

  const minSizeValue = minSize ? minSize : maxSize > 12 ? 12 : 10;

  // Define standard responsive sizes based on common breakpoints
  const xl = maxSize; // 100% of max size for large screens
  const lg = maxSize * 0.9; // 90% for desktop
  const md = maxSize * 0.8; // 80% for tablet
  const sm = maxSize * 0.7; // 70% for large mobile
  const xs = maxSize * 0.6; // 60% for small mobile

  // Apply minimum size constraint
  const xlFinal = Math.max(xl, minSizeValue);
  const lgFinal = Math.max(lg, minSizeValue);
  const mdFinal = Math.max(md, minSizeValue);
  const smFinal = Math.max(sm, minSizeValue);
  const xsFinal = Math.max(xs, minSizeValue);

  return {
    xl: `${pxToRem(xlFinal).toFixed(3)}rem`,
    lg: `${pxToRem(lgFinal).toFixed(3)}rem`,
    md: `${pxToRem(mdFinal).toFixed(3)}rem`,
    sm: `${pxToRem(smFinal).toFixed(3)}rem`,
    xs: `${pxToRem(xsFinal).toFixed(3)}rem`
  };
};

export const fDate = (date: number | Date | string) => {
  return format(new Date(date), 'dd MMM yyyy');
};

export const fPeriod = (endDate: number | Date | string) => {
  const end = new Date(endDate);
  const start = new Date(end);
  start.setMonth(start.getMonth() - 1);
  start.setDate(start.getDate() + 1);

  return `${format(start, 'MMM dd')} – ${format(end, 'MMM dd, yyyy')}`;
};

/**
 * Formats a timestamp to a human-readable "time ago" string.
 *
 * @param timestamp - The timestamp in milliseconds (e.g., Date.now() or Unix timestamp * 1000)
 * @returns A formatted string like "5m ago", "3h ago", or "2d ago"
 *
 * @example
 * formatTimeAgo(Date.now() - 300000) // "5m ago"
 * formatTimeAgo(Date.now() - 7200000) // "2h ago"
 * formatTimeAgo(Date.now() - 172800000) // "2d ago"
 */
export const formatTimeAgo = (timestamp: number): string => {
  if (!timestamp) return 'Unknown';
  const now = Date.now();
  const diff = now - timestamp;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return `${days}d ago`;
};

/**
 * Cleans up DXTrade account names by removing the prefix before the colon.
 * For example: "default:Traders_Connect" becomes "Traders_Connect"
 *
 * @param accountName - The account name to clean up
 * @returns The cleaned account name without the prefix
 *
 * @example
 * formatDXTradeAccount("default:Traders_Connect") // "Traders_Connect"
 * formatDXTradeAccount("default:CFD-3") // "CFD-3"
 * formatDXTradeAccount("RegularAccount") // "RegularAccount"
 */
export const formatDXTradeAccount = (accountName: string): string => {
  if (!accountName) return accountName;
  if (accountName.includes(':')) {
    return accountName.split(':').pop() || accountName;
  }
  return accountName;
};
